<script lang="ts">
  import { getContext } from 'svelte';
  import Loading from '../../../etc/loader.svelte';
  import { loginUser } from '../../../components/auth/LoginUser';
  import RepositoryFactory, { BUNDLE_OPTION } from '../../../repositories/RepositoryFactory';
  import type { GetBundleOptionDisplayResult, GetBundleOptionResult } from '../../../repositories/bundleoption';
  import { endOfMonth, subDays, getDate } from 'date-fns';

  export let mstServiceId: number, serviceClass: string;

  const { close }: any = getContext('simple-modal');
  const bundleOptionRepository = RepositoryFactory[BUNDLE_OPTION];
  // Umobiledまたはエネコムで音声SIMで契約可能オプション群
  const umobiledVoiceSimRegister = [
    'UM_IFILTER',
    'EC_IFILTER',
    'UM_DENWA60',
    'EC_DENWA60',
    'UM_WARIKOMI',
    'EC_WARIKOMI',
    'UM_RUSUDEN',
    'EC_RUSUDEN'
  ];
  // Umobiledまたはエネコムで、データSIMまたはSMS付きSIMで契約可能オプション群
  const umobiledDataSimRegister = ['UM_IFILTER', 'EC_IFILTER'];

  // Umobiledまたはエネコムで、無料期間の注意事項を表示するオプション群
  const umobiledDisplayNotes = [
    'UM_LIFESUPPORT',
    'EC_LIFESUPPORT',
    'UM_PCANDROID',
    'EC_PCANDROID',
    'UM_IFILTER',
    'EC_IFILTER',
    'UM_SMART_USEN',
    'EC_SMART_USEN',
    'UM_TSUNAGARU',
    'EC_TSUNAGARU',
    'UM_ENKAKU',
    'EC_ENKAKU',
    'UM_TAMMATSU',
    'EC_TAMMATSU'
  ];
  const businessCode = $loginUser.businessCode;

  let bundleOption: GetBundleOptionResult,
    bundleOptionDisplay: GetBundleOptionDisplayResult,
    disabledApply = false,
    isDisplayNotes = true,
    license1 = '',
    license2 = '',
    notes: string = '月額料金は初月無料となります(ただし初月解約の場合を除く)',
    promise = getApi();
  let pageStatus: 'INPUT' | 'CONFIRM' | 'COMPLETE' = 'INPUT';
  let terminateDate: string;

  function onConfirm() {
    pageStatus = 'CONFIRM';
  }

  function onComplete() {
    pageStatus = 'COMPLETE';
  }

  function displayErrorAlert(status: number) {
    if (status === 403) {
      alert('セッションが切れました。再度ログインしてください。');
      loginUser.logout();
      location.reload();
    } else {
      disabledApply = true;
      alert('エラーが発生しました。再度やり直してください。');
    }
  }

  async function getApi() {
    // 契約中のbundleOption, 付帯契約一覧を取得
    await bundleOptionRepository
      .getBundleOption({ mstServiceId })
      .then((res) => {
        bundleOption = res;
      })
      .catch((error) => {
        displayErrorAlert(error.response.status);
      });

    // 契約中以外であれば申し込み可能なのでオプション情報を取得
    if (!bundleOption) {
      await bundleOptionRepository
        .getBundleOptionDisplay({ mstServiceId })
        .then((res) => {
          bundleOptionDisplay = res;

          // TODO 以下処理がif文内で色々な変数への設定を行っており複雑化している。改善候補。
          if (businessCode === 'HIKARI_COLLABO') {
            // 光コラボはWEBROOTのみ申込可能
            disabledApply = bundleOptionDisplay.serviceCode === 'WEBROOT' ? false : true;
          } else if (businessCode === 'WITHUNET') {
            // WITHUNEWは申込不可
            disabledApply = true;
          } else if (businessCode === 'UMOBILE_D' || businessCode === 'UMOBILE_D_ENECOM') {
            disabledApply = disabledApplyUmobiled();
            isDisplayNotes = umobiledDisplayNotes.includes(bundleOptionDisplay.serviceCode);
          } else if (businessCode === 'UMOBILE_EA') {
            disabledApply = true;
          } else if (businessCode === 'HIKARI_01') {
            // 光01はPC+Androidセキュリティのみ申込可能
            disabledApply = bundleOptionDisplay.serviceCode === 'HIKARI01_PCANDROID' ? false : true;
          } else if (businessCode === 'UMOBILE_PREMIUM') {
            disabledApply = false;
          } else {
            disabledApply = true;
          }
        })
        .catch((error) => {
          displayErrorAlert(error.response.status);
        });
    } else {
      switch (bundleOption.serviceCode) {
        case 'SMART_USEN':
        case 'EA_SMART_USEN':
        case 'HIKARI01_SMART_USEN':
          license1 = bundleOption.outboundKey1;
          break;

        case 'WEBROOT':
        case 'AOSBOX':
        case 'UM_IFILTER':
        case 'EC_IFILTER':
          license1 = bundleOption.outboundKey2;
          break;
        default:
          break;
      }
      isDisplayNotes = umobiledDisplayNotes.includes(bundleOption.serviceCode);
    }
    return { bundleOption, bundleOptionDisplay };
  }

  function disabledApplyUmobiled() {
    // Umobiled,エネコムの新規申込可否判定
    if (serviceClass === 'VOICE_PLAN') {
      // 音声SIM＝留守電、割込電話、iフィルター、でんわパック、SIM安心サポートのみ新規申込可能
      return !umobiledVoiceSimRegister.includes(bundleOptionDisplay.serviceCode);
    } else {
      // データSIM、SMS付きSIM＝iフィルター、SIM安心サポートのみ新規申込可能
      return !umobiledDataSimRegister.includes(bundleOptionDisplay.serviceCode);
    }
  }

  function isUnavailableApplyPremium() {
    if (businessCode !== 'UMOBILE_PREMIUM') {
      return false;
    }
    // 月末 - 2日
    let targetDay = getDate(subDays(endOfMonth(new Date()), 2));
    let currentDay = getDate(new Date());
    return targetDay <= currentDay;
  }

  // 解約
  async function doTerminate() {
    if (isUnavailableApplyPremium()) {
      alert('月末3日間はオプション変更はできません');
      return;
    }

    await bundleOptionRepository
      .terminateBundleOption({
        bundleOptionId: bundleOption.bundleOptionId,
        serviceCode: bundleOption.serviceCode,
        terminateType: 'INDIVIDUAL'
      })
      .then((res) => {
        terminateDate = res.terminateDate;
        onComplete();
      })
      .catch((error) => {
        displayErrorAlert(error.response.status);
      });
  }
  // 契約
  async function doRegister() {
    switch (bundleOptionDisplay.serviceCode) {
      case 'WEBROOT':
      case 'UM_IFILTER':
      case 'EC_IFILTER':
        registerOemls();
        break;
      case 'UM_PCANDROID':
      case 'EC_PCANDROID':
      case 'HIKARI01_PCANDROID':
        registerKingsoft();
        break;
      case 'UM_DENWA60':
      case 'EC_DENWA60':
        registerDenwapack60();
        break;
      case 'UM_WARIKOMI':
      case 'EC_WARIKOMI':
      case 'PM_WARIKOMI':
      case 'UM_RUSUDEN':
      case 'EC_RUSUDEN':
      case 'PM_RUSUDEN':
        registerAloha();
        break;
      default:
        break;
    }
  }

  async function registerOemls() {
    await bundleOptionRepository
      .registerBundleOptionOemls({
        serviceCode: bundleOptionDisplay.serviceCode
      })
      .then((res) => {
        onComplete();
      })
      .catch((error) => {
        displayErrorAlert(error.response.status);
      });
  }

  async function registerKingsoft() {
    await bundleOptionRepository
      .registerBundleOptionKingsoft({
        serviceCode: bundleOptionDisplay.serviceCode
      })
      .then((res) => {
        onComplete();
      })
      .catch((error) => {
        displayErrorAlert(error.response.status);
      });
  }

  async function registerDenwapack60() {
    await bundleOptionRepository
      .registerBundleOptionDenwapack60({
        serviceCode: bundleOptionDisplay.serviceCode
      })
      .then((res) => {
        onComplete();
      })
      .catch((error) => {
        displayErrorAlert(error.response.status);
      });
  }

  async function registerAloha() {
    if (isUnavailableApplyPremium()) {
      alert('月末3日間はオプション変更はできません');
      return;
    }

    await bundleOptionRepository
      .registerBundleOptionAloha({
        serviceCode: bundleOptionDisplay.serviceCode
      })
      .then((res) => {
        onComplete();
      })
      .catch((error) => {
        displayErrorAlert(error.response.status);
      });
  }
</script>

{#if pageStatus === 'INPUT'}
  {#await promise}
    <Loading />
  {:then { bundleOption, bundleOptionDisplay }}
    <div class="h-auto p-6 bg-white m-auto max-w-4xl">
      <div class="mb-6">
        <div class="container flex justify-between">
          <h1 class="text-2xl font-bold text-gray-700">
            {bundleOptionDisplay ? bundleOptionDisplay.serviceName : bundleOption.serviceName}
          </h1>
        </div>
      </div>

      <div class="shadow rounded bg-gray-50 border-t-4 border-green-400">
        <div class="shadow rounded-lg bg-gray-50 border-1 p-4">
          <div class="flex flex-col">
            {#if bundleOptionDisplay}
              <div class="flex-1 mt-4 mb-10">
                <div class="inline-flex justify-items-center items-center min-w-full">
                  {bundleOptionDisplay.serviceName} を申し込みますか？
                </div>
                月額：{bundleOptionDisplay.amountWithTax}円（税込み）
              </div>
            {:else}
              <div class="flex-1 mt-4 mb-10">
                <div class="grid md:gap-y-2 md:grid-cols-4">
                  <div class="grid-1 col-span-1 justify-self-start min-w-full">
                    <!-- NOTE 2022/04/14 デイリー後に相談
                      項目のサフィックス「：」は、あっても無くてもどちらでもよいという印象なのでこのまま残すことに決定 -->
                    ステータス：
                  </div>
                  <div class="grid col-span-3">
                    <span class="grid-1">{bundleOption.contractStatusCode}</span>
                  </div>
                  <div class="grid-1 col-span-1 justify-self-start min-w-full">申込日：</div>
                  <div class="grid col-span-3">
                    <span class="grid-1">
                      {bundleOption.contractStartDate}
                    </span>
                  </div>
                  {#if license1}
                    <div class="col-span-1 justify-self-start min-w-full">ライセンス情報：</div>
                    <div class="grid col-span-3">
                      <span class="grid-1">{license1}</span>
                      {#if license2}
                        <span class="grid-1">{license2}</span>
                      {/if}
                    </div>
                  {/if}
                </div>
              </div>
            {/if}

            {#if isDisplayNotes}
              <div class="flex-1 mb-10">
                <div class="shadow rounded bg-gray-200 p-4">
                  <slot>
                    注意事項<br />
                    {notes}
                  </slot>
                </div>
              </div>
            {/if}
            <div class="flex-1">
              <div class="flex flex-col justify-items-center items-center">
                <div class="flex-1">
                  {#if bundleOptionDisplay}
                    <button on:click={() => onConfirm()} class="button-primary" disabled={disabledApply}>
                      申込手続きへ
                    </button>
                  {:else if !bundleOption.singleTerminateFlg}
                    <button on:click={() => onConfirm()} class="button-withdrawal"> 解約手続きへ </button>
                  {/if}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  {:catch error}
    <!-- API取得失敗 -->
  {/await}
{:else}
  <div class="h-auto p-6 bg-white m-auto max-w-4xl">
    <div class="mb-6">
      <div class="container flex justify-between">
        <h1 class="text-2xl font-bold text-gray-700">
          {bundleOptionDisplay ? bundleOptionDisplay.serviceName : bundleOption.serviceName}
        </h1>
      </div>
    </div>

    {#if pageStatus === 'CONFIRM'}
      <div class="shadow rounded bg-gray-50 border-t-4 border-green-400">
        <div class="shadow rounded-lg bg-gray-50 border-1 p-4">
          <div class="flex flex-col">
            {#if bundleOptionDisplay}
              <div class="flex-1 mt-4 mb-10">
                <div class="inline-flex justify-items-center items-center min-w-full">
                  申込：{bundleOptionDisplay.serviceName}
                </div>
                月額：{bundleOptionDisplay.amountWithTax}円（税込み）
              </div>
            {:else}
              <div class="flex-1 mt-4 mb-10">
                <div class="inline-flex justify-items-center items-center min-w-full">
                  解約：{bundleOption.serviceName}
                </div>
              </div>
            {/if}
            {#if isDisplayNotes}
              <div class="flex-1 mb-10">
                <div class="shadow rounded bg-gray-200 p-4">
                  <slot>
                    注意事項 <br />
                    {notes}
                  </slot>
                </div>
              </div>
            {/if}

            <div class="flex-1">
              <div class="flex flex-col justify-items-center items-center">
                <div class="flex-1">
                  {#if bundleOptionDisplay}
                    <button on:click|once={() => doRegister()} class="button-primary" disabled={disabledApply}>
                      申し込む
                    </button>
                  {:else if !bundleOption.singleTerminateFlg}
                    <button on:click|once={() => doTerminate()} class="button-withdrawal"> 解約する </button>
                  {/if}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    {:else}
      <div class="shadow rounded bg-gray-50 border-t-4 border-green-400">
        <div class="shadow rounded-lg bg-gray-50 border-1 p-4">
          <div class="flex flex-col">
            <div class="flex-1 mt-4 mb-10">
              <div class="inline-flex justify-items-center items-center min-w-full">
                {#if bundleOptionDisplay}
                  申し込みが完了しました。
                {:else}
                  解約日：{terminateDate}
                {/if}
              </div>
            </div>

            <div class="flex-1">
              <div class="flex flex-col justify-items-center items-center">
                <div class="flex-1">
                  <button on:click|once={() => close()} class="button-withdrawal"> 閉じる </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    {/if}
  </div>
{/if}

<style lang="postcss">
  .button-primary {
    @apply bg-green-400 rounded-lg hover:bg-green-500 focus:outline-none text-sm font-semibold text-white px-10 py-2;
  }
  .button-primary:disabled {
    @apply opacity-30 cursor-default;
  }
  .button-withdrawal {
    @apply bg-red-100 rounded-lg hover:bg-red-200 focus:outline-none text-sm font-semibold text-red-700 px-10 py-2;
  }
</style>
